import React, { useEffect } from 'react';
import { Button, Input, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Column } from '@cards/Card';
import HeaderButton from '@components/HeaderButton';
import { usePageActions, useSearch } from '@hooks';
import { loadDirectChildOrganisationUsers } from '@redux/directChildOrganisationAdministration/actions';
import {
  selectDirectChildOrganisationUsers,
  selectDirectChildOrganisationUsersState,
} from '@redux/directChildOrganisationAdministration/selectors';
import { setCurrentScreen, screens } from '../../../analytics';
import '../directChildOrganisationAdministration.less';
import createColumns from './createColumns';

const matchesUser = (user, comparator) =>
  comparator(user.firstName) ||
  comparator(user.lastName) ||
  comparator(user.email) ||
  comparator(user.organisationName) ||
  comparator(user.role);

const DirectChildOrganisationUserList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const { t } = useTranslation();
  const users = useSelector(selectDirectChildOrganisationUsers);
  const collection = useSelector(selectDirectChildOrganisationUsersState);
  const [searchResults, searchProps] = useSearch(users, matchesUser);
  const title = t('directChildOrganisationAdministration:users.list.title');

  const { hasPageActionsProvider } = usePageActions({
    title,
    actions: [
      <HeaderButton
        key="new-direct-child-organisation-user"
        tooltip={t('directChildOrganisationAdministration:users.list.new')}
        onClick={() => history.push(`${match.url}/new`)}
      >
        {t('directChildOrganisationAdministration:users.list.new')}
      </HeaderButton>,
    ],
  });

  useEffect(() => {
    setCurrentScreen(screens.ADMINISTRATION_DIRECT_CHILD_ORGANISATION_USERS);
  }, []);

  useEffect(() => {
    dispatch(loadDirectChildOrganisationUsers());
  }, [dispatch]);

  const columns = createColumns(t);

  const failureKey =
    collection.errorStatus === 403
      ? 'forbidden'
      : collection.errorStatus === 404
      ? 'notFound'
      : collection.errorStatus === 502
      ? 'upstream'
      : 'failure';

  return (
    <Column>
      {!hasPageActionsProvider ? <h1>{title}</h1> : null}
      <Card.Full className="direct-child-organisation-user-list-card" noPadding>
        <div className="search-container">
          <Input.Search
            aria-label={t('directChildOrganisationAdministration:users.list.search')}
            placeholder={t('directChildOrganisationAdministration:users.list.search')}
            {...searchProps}
          />
          {!hasPageActionsProvider ? (
            <Button type="primary" onClick={() => history.push(`${match.url}/new`)}>
              {t('directChildOrganisationAdministration:users.list.new')}
            </Button>
          ) : null}
        </div>
        {collection.status === 'failure' ? (
          <section role="alert">
            <p>{t(`directChildOrganisationAdministration:users.list.${failureKey}`)}</p>
            <Button onClick={() => dispatch(loadDirectChildOrganisationUsers())}>
              {t('directChildOrganisationAdministration:users.list.retry')}
            </Button>
          </section>
        ) : (
          <Table
            columns={columns}
            dataSource={searchResults}
            loading={collection.status === 'loading'}
            locale={{
              emptyText:
                collection.status === 'success'
                  ? t('directChildOrganisationAdministration:users.list.empty')
                  : null,
            }}
            pagination={{ position: 'bottom' }}
            rowKey={user => `${user.organisationSlug}:${user.id}`}
            scroll={{ x: 1080 }}
            onRow={user => ({
              onClick: () => history.push(`${match.url}/${user.organisationSlug}/${user.id}`),
            })}
          />
        )}
      </Card.Full>
    </Column>
  );
};

export default DirectChildOrganisationUserList;
